import { useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../store/actions/authActions';
import { closeMainModal } from '../store/actions/profileActions';
import environment from '../environments/index';
import { SessionTimeoutHook, SessionWarningState } from '../types/session';

export const useSessionTimeout = (): SessionTimeoutHook => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = useSelector((state: any) => state.general.isAuth);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const shouldLogoutRef = useRef<boolean>(false);
  
  const [sessionState, setSessionState] = useState<SessionWarningState>({
    showWarning: false,
    timeLeft: 0,
    isActive: false,
  });

  const setSessionStateRef = useRef(setSessionState);
  setSessionStateRef.current = setSessionState;
  
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const clearAllTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (shouldLogoutRef.current) {
      if (sessionState.timeLeft === 0 && !sessionState.showWarning) {
        shouldLogoutRef.current = false;
        const timeoutId = setTimeout(() => {
          if (dispatchRef.current && navigateRef.current) {
            dispatchRef.current(closeMainModal());
            (dispatchRef.current as any)(logout(true, navigateRef.current));
          }
        }, 100);
        return () => clearTimeout(timeoutId);
      }
    }
  }, [sessionState.timeLeft, sessionState.showWarning]);

  const startCountdown = useCallback((seconds: number) => {
    setSessionState(prev => {
      return {
        ...prev,
        timeLeft: seconds,
        showWarning: true,
      };
    });

    countdownRef.current = setInterval(() => {
      setSessionState(prev => {
        const newTimeLeft = prev.timeLeft - 1;
        if (newTimeLeft <= 0) {
          clearInterval(countdownRef.current!);
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
          }
          shouldLogoutRef.current = true;
          return {
            ...prev,
            timeLeft: 0,
            showWarning: false,
          };
        }
        return {
          ...prev,
          timeLeft: newTimeLeft,
        };
      });
    }, 1000);
  }, []);

  const resetTimeout = useCallback(() => {
    clearAllTimeouts();
    
    const logoutTime = environment.intervals.logout * 1000;
    const warningTime = logoutTime - 60000;

    warningTimeoutRef.current = setTimeout(() => {
      startCountdown(60);
    }, warningTime);

    timeoutRef.current = setTimeout(() => {
      if (dispatchRef.current) {
        dispatchRef.current(closeMainModal());
      }
      if (setSessionStateRef.current) {
        setSessionStateRef.current(prev => ({
          ...prev,
          showWarning: false,
          timeLeft: 0,
        }));
      }
      if (dispatchRef.current && navigateRef.current) {
        (dispatchRef.current as any)(logout(true, navigateRef.current));
      }
    }, logoutTime);

    if (setSessionStateRef.current) {
      setSessionStateRef.current(prev => ({
        ...prev,
        isActive: true,
      }));
    }
    
  }, [clearAllTimeouts, startCountdown]);

  const extendSession = useCallback(() => {
    shouldLogoutRef.current = false;
    if (dispatchRef.current) {
      dispatchRef.current(closeMainModal());
    }
    if (setSessionStateRef.current) {
      setSessionStateRef.current(prev => ({
        ...prev,
        showWarning: false,
        timeLeft: 0,
      }));
    }
    clearAllTimeouts();
    resetTimeout();
  }, [clearAllTimeouts, resetTimeout]);

  useEffect(() => {
    const isAuthPage = location.pathname.match(/^\/(login|register|forgot-password|bolsadetrabajo|onboarding|onboarding-v2|creartrabajo|$)/);
    const hasTokens = localStorage.getItem("access-Token-Edify");
    const shouldActivateTimeout = (isAuth || hasTokens) && !isAuthPage;
    
    if (!shouldActivateTimeout) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
        warningTimeoutRef.current = null;
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
      return;
    }
    
    const events = ['click', 'keydown', 'scroll', 'touchstart'];
    
    let lastResetTime = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastResetTime > 5000) {
        lastResetTime = now;
        
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        if (warningTimeoutRef.current) {
          clearTimeout(warningTimeoutRef.current);
        }
        if (countdownRef.current) {
          clearInterval(countdownRef.current);
        }
        
        const logoutTime = environment.intervals.logout * 1000;
        const warningTime = logoutTime - 60000;

        warningTimeoutRef.current = setTimeout(() => {
          if (setSessionStateRef.current) {
            setSessionStateRef.current(prev => ({
              ...prev,
              timeLeft: 60,
              showWarning: true,
            }));

            countdownRef.current = setInterval(() => {
              if (setSessionStateRef.current) {
                setSessionStateRef.current(prev => {
                  const newTimeLeft = prev.timeLeft - 1;
                  if (newTimeLeft <= 0) {
                    if (countdownRef.current) {
                      clearInterval(countdownRef.current);
                    }
                    if (timeoutRef.current) {
                      clearTimeout(timeoutRef.current);
                      timeoutRef.current = null;
                    }
                    shouldLogoutRef.current = true;
                    return {
                      ...prev,
                      timeLeft: 0,
                      showWarning: false,
                    };
                  }
                  return {
                    ...prev,
                    timeLeft: newTimeLeft,
                  };
                });
              }
            }, 1000);
          }
        }, warningTime);

        timeoutRef.current = setTimeout(() => {
          if (dispatchRef.current) {
            dispatchRef.current(closeMainModal());
          }
          if (setSessionStateRef.current) {
            setSessionStateRef.current(prev => ({
              ...prev,
              showWarning: false,
              timeLeft: 0,
            }));
          }
          if (dispatchRef.current && navigateRef.current) {
            (dispatchRef.current as any)(logout(true, navigateRef.current));
          }
        }, logoutTime);

        if (setSessionStateRef.current) {
          setSessionStateRef.current(prev => ({
            ...prev,
            isActive: true,
          }));
        }
      }
    };
    
    events.forEach(event => {
      document.addEventListener(event, throttledReset, true);
    });

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    const logoutTime = environment.intervals.logout * 1000;
    const warningTime = logoutTime - 60000;

    warningTimeoutRef.current = setTimeout(() => {
      if (setSessionStateRef.current) {
        setSessionStateRef.current(prev => ({
          ...prev,
          timeLeft: 60,
          showWarning: true,
        }));

        countdownRef.current = setInterval(() => {
          if (setSessionStateRef.current) {
            setSessionStateRef.current(prev => {
              const newTimeLeft = prev.timeLeft - 1;
              if (newTimeLeft <= 0) {
                if (countdownRef.current) {
                  clearInterval(countdownRef.current);
                }
                if (timeoutRef.current) {
                  clearTimeout(timeoutRef.current);
                  timeoutRef.current = null;
                }
                shouldLogoutRef.current = true;
                return {
                  ...prev,
                  timeLeft: 0,
                  showWarning: false,
                };
              }
              return {
                ...prev,
                timeLeft: newTimeLeft,
              };
            });
          }
        }, 1000);
      }
    }, warningTime);

    timeoutRef.current = setTimeout(() => {
      if (dispatchRef.current) {
        dispatchRef.current(closeMainModal());
      }
      if (setSessionStateRef.current) {
        setSessionStateRef.current(prev => ({
          ...prev,
          showWarning: false,
          timeLeft: 0,
        }));
      }
      if (dispatchRef.current && navigateRef.current) {
        (dispatchRef.current as any)(logout(true, navigateRef.current));
      }
    }, logoutTime);

    if (setSessionStateRef.current) {
      setSessionStateRef.current(prev => ({
        ...prev,
        isActive: true,
      }));
    }

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledReset, true);
      });
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (warningTimeoutRef.current) {
        clearTimeout(warningTimeoutRef.current);
      }
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [isAuth, location.pathname]);

  const closeModal = useCallback(() => {
    shouldLogoutRef.current = false;
    if (dispatchRef.current) {
      dispatchRef.current(closeMainModal());
    }
    if (setSessionStateRef.current) {
      setSessionStateRef.current(prev => ({
        ...prev,
        showWarning: false,
        timeLeft: 0,
      }));
    }
  }, []);

  return {
    showWarning: sessionState.showWarning,
    timeLeft: sessionState.timeLeft,
    extendSession,
    resetTimeout,
    onClose: closeModal,
  };
};
